import type { ApplicationConfig } from '@angular/core';
import {
  provideBrowserGlobalErrorListeners,
  isDevMode,
  provideAppInitializer,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslocoHttpLoader } from './core/transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { httpErrorInterceptor } from './core/interceptors/http-error-interceptor';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { provideKeycloakAngular } from '../environments/keycloak.config';
import { IconService } from './core/icon.service';
import { JsonUrlSerializer } from './core/json-url-serializer';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { environment } from '../environments/environment';
import Keycloak from 'keycloak-js';
import { provideServiceWorker } from '@angular/service-worker';
import { AppUpdateService } from './core/app-update.service';
import { setupDayjs } from './shared/utils/setup-dayjs';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideKeycloakAngular(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, httpErrorInterceptor])),
    provideTransloco({
      config: {
        availableLangs: ['en', 'hu'],
        defaultLang: 'en',
        reRenderOnLangChange: true, //futásidőben nyelvváltás
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(async () => {
      inject(IconService).registerIcons();

      const keycloak = inject(Keycloak);
      const authenticated = await keycloak.init(environment.keycloakConfig.initOptions);

      if (authenticated) {
        const roles = keycloak.tokenParsed?.realm_access?.roles ?? [];

        if (roles.length === 0) {
          sessionStorage.setItem('logoutReason', 'no-role');
          //SystemAdministrator csoporthoz nincs role rendelve -> logout
          await keycloak.logout({
            redirectUri: globalThis.location.origin,
          });
          await new Promise(() => {}); // blokkoljuk a bootstrapot a redirect alatt
        }
      }
    }),
    { provide: UrlSerializer, useClass: JsonUrlSerializer },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAppInitializer(() => inject(AppUpdateService).init()),
    provideAppInitializer(() => setupDayjs()),
  ],
};
